import { getBaseRole } from '@/helpers/customRoles';
import {
  useCallback,
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
} from 'react'
import { crmApi, crmLeadParentApi, crmTrafficChannelApi } from '@/api'
import type { Field } from '@/api/types/crm/crm.types'
import type {
  CrmLeadParent,
  CrmLeadParentIntegrationFieldsData,
} from '@/api/types/crm/crmLeadParent.types'
import type { CrmLeadCardInFunnelData } from '@/api/types/crm/crmFunnels.types'
import { isError } from '@/helpers/checkQuery'
import { useAddAlert } from '@/hooks/useAddAlert'
import { getLeadFieldValue } from '../../../utils/leadFields'
import {
  appendCrmLeadFieldBindingSettingsHint,
  extractCrmLeadFieldSaveErrorMessages,
  isMessengerIdentityFieldUniqueName,
  mapMessengerIdentityFieldSaveErrors,
  type MessengerIdentityFieldUniqueName,
} from '../../../utils/messengerAccountFields'
import {
  ignoreLeadFields,
  validateForm,
} from '../../AddNewLeadSelect/validateLeadData'
import type { LeadProfileLead } from '../LeadProfileModal.types'
import { toNextContactApiValue } from './leadProfileContent.utils'
import {
  dealUtmEquals,
  dealUtmToApiPayload,
  normalizeDealUtm,
  type DealUtm,
} from './dealUtm.utils'
import { isUtmLeadField } from '@pages/Admin/CRM/utils/utmLeadFields'
import {
  composeCrmLeadIntegrationLinkFromInput,
  getCrmLeadIntegrationFieldUrlPrefix,
  isCrmLeadIntegrationField,
} from '@pages/Admin/CRM/utils/crmLeadIntegrationFields'
import { normalizeCrmLeadFormFieldValue } from '@pages/Admin/CRM/utils/crmLeadFieldValues'
import { replaceCrmLeadParentInList } from '../blocks/MainInfoBlock/LeadIntegrationFieldsBlock/leadIntegrationContext.utils'

export function useLeadProfileSave(params: {
  lead: LeadProfileLead | undefined
  currentRole: string
  canEditFields: boolean
  canChangeLeadStatus: boolean
  leadFields: Field[]
  formData: Record<string, string>
  setFormData: Dispatch<SetStateAction<Record<string, string>>>
  setIdentityFieldErrors: Dispatch<
    SetStateAction<Partial<Record<MessengerIdentityFieldUniqueName, string>>>
  >
  selectedFunnelId: number | null
  selectedFunnelIdRef: MutableRefObject<number | null>
  selectedLeadFunnelId: number | null
  multipleDealsEnabled: boolean
  committedManagerId: number | null
  committedNextContactAt: string | null
  committedCurrentStatusId: number | null
  committedComment: string | null
  selectedManagerIdRef: MutableRefObject<number | null>
  isManagerExplicitlyClearedRef: MutableRefObject<boolean>
  trafficChannelDraftId: number | null
  funnelNextContactDraftRef: MutableRefObject<string>
  dealCommentDraftRef: MutableRefObject<string>
  dealUtmDraft: DealUtm
  committedDealUtm: DealUtm
  latestFunnelStatusIdRef: MutableRefObject<number | null>
  statusListRefreshRef: MutableRefObject<(() => void) | null>
  resolveLeadFunnelId: () => number | undefined
  getCardInFunnelSnapshot: (
    funnelId: number,
    leadFunnelId?: number | null,
  ) => CrmLeadCardInFunnelData | undefined
  handleRefetchLeadInModal: (
    funnelId?: number | null,
  ) => Promise<LeadProfileLead | undefined>
  handleRefetchLeadsOnClose: () => void
  syncKanbanCardAfterMutation: () => void
  buildFormDataFromLead: (
    leadSource: LeadProfileLead | undefined,
  ) => Record<string, string>
  setHistoryRefreshKey: Dispatch<SetStateAction<number>>
  /** Dirty integration-поля родителей: parentId → fieldId → value. */
  parentIntegrationDrafts?: Record<number, Record<string, string>>
  clearParentIntegrationDrafts?: () => void
  /** Подставить `parent` из PUT integration-fields (чаты/values). */
  patchLead?: (partial: Partial<LeadProfileLead>) => void
}) {
  const addAlert = useAddAlert()
  const {
    lead,
    currentRole,
    canEditFields,
    canChangeLeadStatus,
    leadFields,
    formData,
    setFormData,
    setIdentityFieldErrors,
    selectedFunnelId,
    selectedFunnelIdRef,
    selectedLeadFunnelId,
    multipleDealsEnabled,
    committedNextContactAt,
    committedCurrentStatusId,
    committedComment,
    selectedManagerIdRef,
    isManagerExplicitlyClearedRef,
    trafficChannelDraftId,
    funnelNextContactDraftRef,
    dealCommentDraftRef,
    dealUtmDraft,
    committedDealUtm,
    latestFunnelStatusIdRef,
    statusListRefreshRef,
    resolveLeadFunnelId,
    getCardInFunnelSnapshot,
    handleRefetchLeadInModal,
    handleRefetchLeadsOnClose,
    syncKanbanCardAfterMutation,
    buildFormDataFromLead,
    setHistoryRefreshKey,
    parentIntegrationDrafts = {},
    clearParentIntegrationDrafts,
    patchLead,
  } = params

  return useCallback(async (options?: { updateIntegrations?: boolean }) => {
    if (!lead) {
      addAlert({
        title: 'Ошибка',
        text: 'Не удалось сохранить данные, лид не найден',
        type: 'error',
      })
      return false
    }

    const editableFields = leadFields.filter((field) => {
      if (ignoreLeadFields.includes(field.unique_name)) return false
      // Сделки ON — UTM_* анкеты не валидируем и не шлём; пишем utm сделки.
      if (multipleDealsEnabled && isUtmLeadField(field)) return false
      return true
    })

    const canEditManagerInCard = ['admin', 'head-of-sales'].includes(
      getBaseRole(currentRole),
    )
    const shouldUpdateManager =
      canEditManagerInCard && selectedFunnelId !== null
    const resolvedFunnelId: number | null =
      selectedFunnelIdRef.current !== null &&
      Number.isFinite(selectedFunnelIdRef.current) &&
      selectedFunnelIdRef.current > 0
        ? selectedFunnelIdRef.current
        : null
    const draftStatusId =
      latestFunnelStatusIdRef.current ?? committedCurrentStatusId

    const resultValidation = validateForm(formData, editableFields, true, {
      funnelId: resolvedFunnelId ?? selectedFunnelId,
      statusId: draftStatusId,
    })
    if (resultValidation !== true) {
      const leadFieldsByUniqueName = new Map(
        leadFields.map((field) => [field.unique_name, field.name]),
      )
      const nextIdentityFieldErrors = Object.fromEntries(
        Object.entries(resultValidation).filter(([key]) =>
          isMessengerIdentityFieldUniqueName(key),
        ),
      ) as Partial<Record<MessengerIdentityFieldUniqueName, string>>

      if (Object.keys(nextIdentityFieldErrors).length > 0) {
        setIdentityFieldErrors((prev) => ({
          ...prev,
          ...nextIdentityFieldErrors,
        }))
      }

      addAlert({
        title: 'Ошибка',
        text: Object.entries(resultValidation).map(
          ([key, value]) =>
            `${leadFieldsByUniqueName.get(key) || key}: ${value}`,
        ),
        type: 'error',
      })
      return false
    }
    const editableFieldNames = new Set(
      editableFields.map((field) => field.unique_name),
    )
    const fieldsToSubmit = Object.fromEntries(
      Object.entries(formData)
        .filter(([key]) => editableFieldNames.has(key))
        .map(([key, value]) => {
          const field = leadFields.find((item) => item.unique_name === key)
          if (!field) {
            return [key, value] as const
          }

          if (field.type === 'boolean' || field.type === 'file') {
            return [
              key,
              normalizeCrmLeadFormFieldValue(field.type, value),
            ] as const
          }

          if (!isCrmLeadIntegrationField(field)) {
            return [key, value] as const
          }

          const prefix = getCrmLeadIntegrationFieldUrlPrefix(field)
          return [
            key,
            composeCrmLeadIntegrationLinkFromInput(value, prefix),
          ] as const
        }),
    )
    // ФИО правится в шапке карточки и не входит в поля воронки (в «Учениках»
    // поля NAME нет), поэтому шлём его отдельно и только если оно менялось.
    if (!editableFieldNames.has('NAME') && formData['NAME'] !== undefined) {
      const committedName = String(getLeadFieldValue(lead, 'NAME') ?? '')
      if (formData['NAME'] !== committedName) {
        fieldsToSubmit['NAME'] = formData['NAME']
      }
    }
    const updateLeadParams = {
      lead_id: lead.id,
      ...(resolvedFunnelId !== null ? { funnel_id: resolvedFunnelId } : {}),
    }

    const committedNextContactRaw = committedNextContactAt

    const draftNextContactRaw =
      funnelNextContactDraftRef.current?.trim() ?? ''
    const committedApi = toNextContactApiValue(committedNextContactRaw)
    const draftApi = toNextContactApiValue(
      draftNextContactRaw === '' ? null : draftNextContactRaw,
    )
    const hasSelectedDeal =
      selectedLeadFunnelId != null &&
      Number.isFinite(selectedLeadFunnelId) &&
      selectedLeadFunnelId > 0
    const shouldUpdateNextContactAt =
      canEditFields &&
      selectedFunnelId !== null &&
      Number.isFinite(Number(selectedFunnelId)) &&
      Number(selectedFunnelId) > 0 &&
      draftApi !== committedApi &&
      (multipleDealsEnabled ? hasSelectedDeal : true)

    const funnelCardPatch: {
      manager_id?: number | null
      next_contact_at?: string | null
      traffic_channel_id?: number | null
      current_status_id?: number
      comment?: string | null
      utm?: ReturnType<typeof dealUtmToApiPayload>
    } = {}

    if (shouldUpdateManager) {
      funnelCardPatch.manager_id = isManagerExplicitlyClearedRef.current
        ? null
        : selectedManagerIdRef.current ?? null
    }
    if (shouldUpdateNextContactAt) {
      funnelCardPatch.next_contact_at = draftApi
    }

    const normalizeDealComment = (raw: string | null | undefined) => {
      if (raw == null) return null
      const trimmed = String(raw).trim()
      return trimmed === '' ? null : trimmed
    }
    const draftCommentApi = normalizeDealComment(
      dealCommentDraftRef.current,
    )
    const committedCommentApi = normalizeDealComment(committedComment)
    const shouldUpdateComment =
      multipleDealsEnabled &&
      canEditFields &&
      selectedFunnelId !== null &&
      Number.isFinite(Number(selectedFunnelId)) &&
      Number(selectedFunnelId) > 0 &&
      selectedLeadFunnelId != null &&
      Number.isFinite(selectedLeadFunnelId) &&
      selectedLeadFunnelId > 0 &&
      draftCommentApi !== committedCommentApi

    if (shouldUpdateComment) {
      funnelCardPatch.comment = draftCommentApi
    }

    const draftUtmApi = normalizeDealUtm(dealUtmDraft)
    const committedUtmApi = normalizeDealUtm(committedDealUtm)
    const shouldUpdateDealUtm =
      multipleDealsEnabled &&
      canEditFields &&
      selectedFunnelId != null &&
      Number.isFinite(Number(selectedFunnelId)) &&
      Number(selectedFunnelId) > 0 &&
      selectedLeadFunnelId != null &&
      Number.isFinite(selectedLeadFunnelId) &&
      selectedLeadFunnelId > 0 &&
      !dealUtmEquals(draftUtmApi, committedUtmApi)

    if (shouldUpdateDealUtm) {
      funnelCardPatch.utm = dealUtmToApiPayload(draftUtmApi)
    }

    const snapTrafficChannelId =
      selectedFunnelId != null
        ? (() => {
            const snap = getCardInFunnelSnapshot(
              selectedFunnelId,
              selectedLeadFunnelId,
            )
            const fromId = snap?.traffic_channel_id
            if (fromId != null && Number.isFinite(Number(fromId))) {
              return Number(fromId)
            }
            const objId = snap?.traffic_channel?.id
            return objId != null && Number.isFinite(Number(objId))
              ? Number(objId)
              : null
          })()
        : null

    const committedTrafficChannelId = multipleDealsEnabled
      ? snapTrafficChannelId ?? lead.traffic_channel?.id ?? null
      : lead.traffic_channel?.id ?? null
    const shouldUpdateTrafficChannel =
      canEditFields &&
      trafficChannelDraftId != null &&
      trafficChannelDraftId !== committedTrafficChannelId

    if (multipleDealsEnabled && shouldUpdateTrafficChannel) {
      funnelCardPatch.traffic_channel_id = trafficChannelDraftId
    }

    const committedStatusId = committedCurrentStatusId
    const leadFunnelRowId = resolveLeadFunnelId()

    const shouldApplyStatusChange =
      canChangeLeadStatus &&
      draftStatusId !== null &&
      Number.isFinite(draftStatusId) &&
      draftStatusId > 0 &&
      draftStatusId !== committedStatusId &&
      (multipleDealsEnabled
        ? selectedFunnelId != null && hasSelectedDeal
        : leadFunnelRowId !== undefined)

    // Сделки ON — статус в том же PATCH card-funnel (+ lead_funnel_id), как канал трафика.
    if (multipleDealsEnabled && shouldApplyStatusChange) {
      funnelCardPatch.current_status_id = draftStatusId
    }

    const shouldPatchFunnelCard =
      selectedFunnelId !== null &&
      Number.isFinite(Number(selectedFunnelId)) &&
      Number(selectedFunnelId) > 0 &&
      Object.keys(funnelCardPatch).length > 0

    const cardFunnelUpdateParams = {
      lead_id: lead.id,
      funnel_id: selectedFunnelId!,
      ...(multipleDealsEnabled && hasSelectedDeal
        ? { lead_funnel_id: selectedLeadFunnelId }
        : {}),
    }

    const parentFieldRequests: Array<
      ReturnType<typeof crmLeadParentApi.updateIntegrationFields>
    > = []
    if (canEditFields) {
      for (const [parentIdRaw, draft] of Object.entries(
        parentIntegrationDrafts,
      )) {
        const parentId = Number(parentIdRaw)
        if (!Number.isFinite(parentId) || parentId <= 0) continue
        if (!draft || Object.keys(draft).length === 0) continue

        const fields: Record<string, string> = {}
        for (const [fieldId, rawValue] of Object.entries(draft)) {
          const field = leadFields.find(
            (item) => String(item.id) === String(fieldId),
          )
          if (!field || !isCrmLeadIntegrationField(field)) {
            fields[fieldId] = rawValue
            continue
          }
          const prefix = getCrmLeadIntegrationFieldUrlPrefix(field)
          fields[fieldId] = composeCrmLeadIntegrationLinkFromInput(
            rawValue,
            prefix,
          )
        }

        parentFieldRequests.push(
          crmLeadParentApi.updateIntegrationFields({
            lead_id: lead.id,
            parent_id: parentId,
            fields,
          }),
        )
      }
    }

    const leadRequests = [
      crmApi.updateLeadFields(updateLeadParams, {
        fields: fieldsToSubmit,
        // PHONE почти всегда в payload — без явного false бэк (default true) перезапишет интеграции.
        update_integrations: options?.updateIntegrations ?? false,
      }),
      ...(shouldPatchFunnelCard
        ? [
            crmApi.leads.updateLeadCardInFunnel(
              cardFunnelUpdateParams,
              funnelCardPatch,
            ),
          ]
        : []),
      ...(!multipleDealsEnabled && shouldUpdateTrafficChannel
        ? [
            crmTrafficChannelApi.updateLeadChannel(
              lead.id,
              trafficChannelDraftId,
            ),
          ]
        : []),
    ]

    try {
      const [leadResponses, parentResponses] = await Promise.all([
        Promise.all(leadRequests),
        Promise.all(parentFieldRequests),
      ])
      const responses = [...leadResponses, ...parentResponses]
      const failedResponse = responses.find((res) => isError(res))
      if (failedResponse) {
        const apiMessages = appendCrmLeadFieldBindingSettingsHint(
          extractCrmLeadFieldSaveErrorMessages({
            response: { data: failedResponse.data },
          }),
        )
        const mappedErrors = mapMessengerIdentityFieldSaveErrors(apiMessages)
        if (Object.keys(mappedErrors).length > 0) {
          setIdentityFieldErrors((prev) => ({ ...prev, ...mappedErrors }))
        }

        throw new Error(apiMessages[0] || 'Ошибка обновления данных')
      }

      let parentSyncFailed = false
      let parentMissingInResponse = false
      let nextParents = Array.isArray(lead.parents)
        ? ([...lead.parents] as CrmLeadParent[])
        : []

      for (const res of parentResponses) {
        const data = (res as { data?: { data?: CrmLeadParentIntegrationFieldsData } })
          ?.data?.data
        if (!data) continue
        if (data.integration_synchronized === false) {
          parentSyncFailed = true
        }
        if (data.parent) {
          nextParents = replaceCrmLeadParentInList(nextParents, data.parent)
        } else {
          parentMissingInResponse = true
          if (data.parent_id && data.integration_field_values) {
            nextParents = nextParents.map((item) =>
              Number(item.id) === Number(data.parent_id)
                ? {
                    ...item,
                    integration_field_values: data.integration_field_values,
                  }
                : item,
            )
          }
        }
      }

      if (parentFieldRequests.length > 0 && patchLead) {
        patchLead({ parents: nextParents })
      }

      // Сделки OFF — менеджер должен сохраниться до смены статуса:
      // API запрещает менять статус нераспределённой заявки.
      if (!multipleDealsEnabled && shouldApplyStatusChange) {
        const ins = await crmApi.insertLeadStatus({
          status_id: draftStatusId,
          lead_funnel_id: leadFunnelRowId!,
        })
        if (isError(ins)) throw new Error()
        statusListRefreshRef.current?.()
      }

      addAlert({
        title: 'Успешно',
        text: parentSyncFailed
          ? 'Данные сохранены, чат родителя не обновился'
          : 'Данные успешно обновлены',
        type: 'success',
      })
      setIdentityFieldErrors({})
      clearParentIntegrationDrafts?.()
      if (shouldApplyStatusChange) {
        if (multipleDealsEnabled) {
          statusListRefreshRef.current?.()
        }
        setHistoryRefreshKey((prev) => prev + 1)
      }
      // parent: null в PUT → полный get-lead; иначе refetch всё равно синхронизирует карточку.
      const refetchedLead = await handleRefetchLeadInModal(selectedFunnelId)
      if (refetchedLead) {
        setFormData(buildFormDataFromLead(refetchedLead))
      } else if (parentMissingInResponse) {
        // refetch не вернул лид — уже применили values локально выше.
      }
      handleRefetchLeadsOnClose()
      syncKanbanCardAfterMutation()
      return true
    } catch (error) {
      const apiMessages = appendCrmLeadFieldBindingSettingsHint(
        extractCrmLeadFieldSaveErrorMessages(error),
      )
      const mappedErrors = mapMessengerIdentityFieldSaveErrors(apiMessages)
      if (Object.keys(mappedErrors).length > 0) {
        setIdentityFieldErrors((prev) => ({ ...prev, ...mappedErrors }))
      }

      addAlert({
        title: 'Ошибка',
        text:
          apiMessages.join('\n') ||
          (error instanceof Error ? error.message : '') ||
          'Ошибка обновления данных',
        type: 'error',
      })
      return false
    }
  }, [
    addAlert,
    buildFormDataFromLead,
    canChangeLeadStatus,
    canEditFields,
    committedCurrentStatusId,
    committedNextContactAt,
    committedComment,
    currentRole,
    formData,
    funnelNextContactDraftRef,
    dealCommentDraftRef,
    dealUtmDraft,
    committedDealUtm,
    getCardInFunnelSnapshot,
    handleRefetchLeadInModal,
    handleRefetchLeadsOnClose,
    isManagerExplicitlyClearedRef,
    latestFunnelStatusIdRef,
    lead,
    leadFields,
    multipleDealsEnabled,
    resolveLeadFunnelId,
    selectedFunnelId,
    selectedFunnelIdRef,
    selectedLeadFunnelId,
    selectedManagerIdRef,
    setFormData,
    setHistoryRefreshKey,
    setIdentityFieldErrors,
    statusListRefreshRef,
    syncKanbanCardAfterMutation,
    trafficChannelDraftId,
    parentIntegrationDrafts,
    clearParentIntegrationDrafts,
    patchLead,
  ])
}
